import { FormItem } from 'hic-components'
import { PersonIdentificationDocumentFragment } from 'src/api/api.generated'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { Grid } from '@vapor/material'
import { TextField } from '@mui/material'
import IdentificationDocumentInput from './identification-document-input'
import CountryInput from '../adress-input/country-input'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { addYears } from 'src/utils/date'

export type PersonID = Partial<PersonIdentificationDocumentFragment> | null

export type PersonIdentificationDocumentInputProps = {
  value?: PersonID
  onChange?: (value: PersonID) => void
  disabled?: boolean
  fieldsSmall?: boolean
  error?: {
    documentId?: { message?: string }
    number?: { message?: string }
    releaseBy?: { message?: string }
    expirationDate?: { message?: string }
    countryId?: { message?: string }
    city?: { message?: string }
  }
}

export default function PersonIdentificationDocumentInput(props: PersonIdentificationDocumentInputProps) {
  const { value, onChange, disabled, error, fieldsSmall } = props
  const { t } = useTranslation()

  const [localValue, setLocalValue] = useState<PersonID>(value || null)

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(localValue)) {
      setLocalValue(value)
    }
  }, [value])

  const onLocalChange: (newLocalValue: (prev: PersonID) => PersonID) => void = useCallback(
    (getNewLocalValue: (prev: PersonID) => PersonID) => {
      let newLocalValue = getNewLocalValue(localValue)
      const values = newLocalValue ? Object.values(newLocalValue) : []
      if (!values || values.length === 0 || !values.find((v) => (v ? true : false))) {
        newLocalValue = null
      }
      if (
        //
        (!value && JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) ||
        (value && JSON.stringify(newLocalValue) !== JSON.stringify(value))
      ) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
    },
    [localValue, value, onChange],
  )

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.type')} fullWidth errorMessage={error?.documentId?.message}>
          <IdentificationDocumentInput //
            disabled={disabled}
            value={localValue?.documentId ? { id: localValue.documentId } : null}
            onChange={(value) => onLocalChange((prev) => ({ ...prev, documentId: value?.id }))}
            error={error?.documentId ? true : false}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.number')} fullWidth errorMessage={error?.number?.message}>
          <TextField //
            disabled={disabled}
            value={((localValue?.number as string) || '').toUpperCase()}
            onChange={(event) => onLocalChange((prev) => ({ ...prev, number: event.target.value }))}
            error={error?.number ? true : false}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.release_by')} fullWidth errorMessage={error?.releaseBy?.message}>
          <TextField //
            disabled={disabled}
            value={localValue?.releaseBy || ''}
            onChange={(event) => onLocalChange((prev) => ({ ...prev, releaseBy: event.target.value }))}
            error={error?.releaseBy ? true : false}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
      <Grid item xs={12} md={3}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.expire_date')} fullWidth errorMessage={error?.expirationDate?.message}>
          <DatePicker //
            disabled={disabled}
            value={localValue?.expirationDate || null}
            maxDate={addYears(new Date(), 10)}
            onChange={(date) => {
              if (typeof date !== 'string') {
                onLocalChange((prev) => ({ ...prev, expirationDate: date || undefined }))
              }
            }}
            slotProps={{
              textField: {
                error: error?.expirationDate ? true : false,
                size: fieldsSmall ? 'small' : undefined,
              },
            }}
          />
        </FormItem>
      </Grid>
      <Grid item xs={12} md={3}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.country')} fullWidth errorMessage={error?.countryId?.message}>
          <CountryInput //
            disabled={disabled}
            value={localValue?.countryId ? { id: localValue.countryId } : null}
            onChange={(value) => onLocalChange((prev) => ({ ...prev, countryId: value?.id }))}
            error={error?.countryId ? true : false}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
      <Grid item xs={6}>
        <FormItem fieldLabel={t('pms.common.customer.identification_document.city')} fullWidth errorMessage={error?.city?.message}>
          <TextField //
            disabled={disabled}
            value={localValue?.city || ''}
            onChange={(event) => onLocalChange((prev) => ({ ...prev, city: event.target.value }))}
            error={error?.city ? true : false}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
    </Grid>
  )
}
